import { normalizeQuestionnaireLogicalIdForComparison } from "src-next/modules/patient-summary/transformers/questionnaireResponseExclusions";

/**
 * Estrutura persistida em `AuxiliarData.ProtocolResponse` para questionários dentro do protocolo:
 * - item com `linkId: "protocol-questionnaire"` e sub-itens `questionnaireId`, `questionnaireTitle`, etc.
 * - nome do protocolo no item com `linkId: "438586562091"` (mesmo critério de `ManageProtocol.tsx`).
 *
 * Limitação: só reflete protocolos da organização atual (author Organization/{id}), como em `ManagerProtocol`.
 */

/** Nome do protocolo (campo "nomeProtocolo" no formulário). */
export const PROTOCOL_DISPLAY_NAME_LINK_ID = "438586562091";

/** Grupo que representa um questionário associado ao protocolo. */
export const PROTOCOL_QUESTIONNAIRE_GROUP_LINK_ID = "protocol-questionnaire";

/** Id lógico do recurso FHIR Questionnaire gravado no protocolo. */
export const PROTOCOL_QUESTIONNAIRE_ID_LINK_ID = "questionnaireId";

function firstAnswerValueString(item: unknown): string | undefined {
  if (item == null || typeof item !== "object") return undefined;
  const answers = (item as { answer?: unknown[] }).answer;
  if (!Array.isArray(answers) || answers.length === 0) return undefined;
  const a = answers[0] as { valueString?: unknown };
  if (a?.valueString == null) return undefined;
  const s = String(a.valueString).trim();
  return s.length > 0 ? s : undefined;
}

/**
 * Percorre itens de um QuestionnaireResponse de protocolo e acumula pares
 * (id de questionário normalizado → nome de exibição do protocolo).
 */
function collectFromProtocolResource(
  resource: fhir5.QuestionnaireResponse,
  byQuestionnaire: Map<string, Set<string>>,
): void {
  const protocolResponseId = resource.id?.trim();
  const items = resource.item;
  if (!Array.isArray(items)) return;

  const nameItem = items.find(
    (it) => (it as { linkId?: string }).linkId === PROTOCOL_DISPLAY_NAME_LINK_ID,
  );
  const protocolLabel =
    firstAnswerValueString(nameItem) ||
    (protocolResponseId && protocolResponseId.length > 0
      ? protocolResponseId
      : "Protocolo");

  for (const raw of items) {
    const it = raw as { linkId?: string; item?: unknown[] };
    if (it.linkId !== PROTOCOL_QUESTIONNAIRE_GROUP_LINK_ID) continue;
    const sub = Array.isArray(it.item) ? it.item : [];
    const qRow = sub.find(
      (s) => (s as { linkId?: string }).linkId === PROTOCOL_QUESTIONNAIRE_ID_LINK_ID,
    );
    const questionnaireRaw = firstAnswerValueString(qRow);
    if (!questionnaireRaw) continue;
    const normalized =
      normalizeQuestionnaireLogicalIdForComparison(questionnaireRaw);
    if (!normalized) continue;
    if (!byQuestionnaire.has(normalized)) {
      byQuestionnaire.set(normalized, new Set());
    }
    byQuestionnaire.get(normalized)!.add(protocolLabel);
  }
}

/**
 * Constrói mapa: id lógico de questionário (normalizado para comparação) → lista ordenada de nomes de protocolos.
 * Entradas vêm do bundle retornado por `GetPrescriptionProtocol` com `PROTOCOL_ID` e author da organização.
 */
export function buildQuestionnaireToProtocolNamesMap(
  bundleEntries: fhir5.BundleEntry[] | undefined | null,
): Map<string, string[]> {
  const byQuestionnaire = new Map<string, Set<string>>();

  for (const entry of bundleEntries ?? []) {
    const resource = entry?.resource;
    if (!resource || resource.resourceType !== "QuestionnaireResponse") continue;
    collectFromProtocolResource(resource as fhir5.QuestionnaireResponse, byQuestionnaire);
  }

  const result = new Map<string, string[]>();
  for (const [key, set] of byQuestionnaire) {
    result.set(key, [...set].sort((a, b) => a.localeCompare(b, "pt-BR")));
  }
  return result;
}
